import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useParams } from "react-router";
import {
  GetPatientTreatmentEquipmentReponse,
  GetPatientTreatmentMedicationReponse,
  PatientTreatmentsApi,
} from "../../../api/medical-operations";
import MUIDataTable from "mui-datatables";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { configState } from "../../../recoil/atoms";
import ViewFile from "../../../utils/FormFields/ViewFile";
import LinearProgress from "@mui/material/LinearProgress";
import { Button, Divider, Grid, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../utils/FormFields/ConfirmDialog";
import useAuthorization from "../../Authentication/permissions/Authorization";

export default function PatientTreatments() {
  const config = useRecoilValue(configState);
  let { patientID, appointmentID } = useParams();

  const [rowID, setRowID] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [EquipmentTreatments, setEquipmentTreatments] = useState<GetPatientTreatmentEquipmentReponse[] | any[]>([]);
  const [MedicationTreatments, setMedicationTreatments] = useState<GetPatientTreatmentMedicationReponse[] | any[]>([]);

  const { canDeletePatientTreatmentById, canGetAllPatientTreatmentsOfDiagnosis } = useAuthorization();

  const getPatientTreatments = () => {
    const PatientTreatments = new PatientTreatmentsApi(config);
    PatientTreatments.getAllPatientTreatmentsOfDiagnosis(patientID, undefined, appointmentID)
      .then((res) => {        
        let eq: any = [];
        let med: any = [];
        res.data.map((treatment) => {
          return treatment.Type === "Equipment" ? (eq = [...eq, treatment]) : (med = [...med, treatment]);
        });
        setEquipmentTreatments(eq);
        setMedicationTreatments(med);
      })
      .catch((err) => {        
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getPatientTreatments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deletePatientTreatment(rowID);
    handleCloseDialog();
  };

  const deletePatientTreatment = (id: string) => {
    const PatientTreatments = new PatientTreatmentsApi(config);
    PatientTreatments.deletePatientTreatmentById(id)
      .then((res) => {
        getPatientTreatments();
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const expandedEquipmentColumns = [
    {
      name: "PatientTreatmentID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },

    {
      name: "Equipment.Name",
      label: "Name",
    },
    {
      name: "Equipment.Code",
      label: "Code",
    },
    {
      name: "Equipment.Brand",
      label: "Brand",
    },
    {
      name: "Equipment.Price",
      label: "Price",
    },    
    {
      name: "PrescriptionImage",
      label: "Prescription Image",
      options: {
        customBodyRender: (val: any) => {
          return <ViewFile value={val} />;
        },
      },
    },
    {
      name: "ImageCategory",
      label: "Category",
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
            {canDeletePatientTreatmentById && (
              <Tooltip title="Delete">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                  }}
                  onClick={() => {
                    handleDelete(tableMeta.rowData[0]);
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </Button>
              </Tooltip>
            )}
          </>
        ),
      },
    },
  ];

  const expandedMedicationColumns = [
    {
      name: "PatientTreatmentID",
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: "Medication.Name",
      label: "Name",
    },
    {
      name: "Medication.Code",
      label: "Code",
    },
    {
      name: "Medication.Brand",
      label: "Brand",
    },
    {
      name: "Medication.ActiveIngredient",
      label: "Active Ingredient",
    },
    {
      name: "Medication.Strength",
      label: "Strength",
    },
    {
      name: "Medication.Source",
      label: "Source",
    },
    {
      name: "Medication.BatchNumber",
      label: "Batch Number",
    },
    {
      name: "Medication.Type",
      label: "Type",
    },
    {
      name: "Medication.Dose",
      label: "Dose",
    },
    {
      name: "Medication.Price",
      label: "Price",
    },
    {
      name: "Medication.StartDate",
      label: "Start Date",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => <>{value.slice(0, 10)}</>,
      },
    },
    {
      name: "Medication.EndDate",
      label: "End Date",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => <>{value.slice(0, 10)}</>,
      },
    },
    {
      name: "PrescriptionImage",
      label: "Prescription Image",
      options: {
        customBodyRender: (val: any) => {
          return <ViewFile value={val} />;
        },
      },
    },
    {
      name: "ImageCategory",
      label: "Category",
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
            <Tooltip title="Delete">
              <Button
                variant="outlined"
                color="error"
                sx={{
                  ml: { xs: 0, xl: 2 },
                }}
                onClick={() => {
                  handleDelete(tableMeta.rowData[0]);
                }}
              >
                <DeleteOutlineIcon color="error" />
              </Button>
            </Tooltip>
          </>
        ),
      },
    },
  ];

  return (
    <>
      {canGetAllPatientTreatmentsOfDiagnosis && (
        <MainCard>
          <Grid>
            {!isLoading ? (
              <>
                <Typography variant="h3" gutterBottom>
                  Equipment
                </Typography>
                <Divider />
                <MUIDataTable
                  data={EquipmentTreatments}
                  columns={expandedEquipmentColumns}
                  options={{
                    filter: true,
                    viewColumns: false,
                    selectableRows: "none",
                    selectableRowsOnClick: false,
                    elevation: 0,
                    rowsPerPage: 10,
                    filterType: "dropdown",
                    enableNestedDataAccess: ".",
                    responsive: "standard",
                    pagination: false,
                  }}
                  title=""
                />
                <Typography variant="h3" gutterBottom sx={{ mt: 6 }}>
                  Medication
                </Typography>
                <Divider />
                <MUIDataTable
                  data={MedicationTreatments}
                  columns={expandedMedicationColumns}
                  options={{
                    filter: true,
                    viewColumns: false,
                    selectableRows: "none",
                    selectableRowsOnClick: false,
                    elevation: 0,
                    rowsPerPage: 10,
                    filterType: "dropdown",
                    enableNestedDataAccess: ".",
                    responsive: "standard",
                    pagination: false,
                  }}
                  title=""
                />
                <ConfirmDialog
                  name={"Patient Treatment"}
                  handleCloseDialog={handleCloseDialog}
                  handleConfirm={handleConfirm}
                  openDialog={openDialog}
                />
              </>
            ) : (
              <LinearProgress color="secondary" />
            )}
          </Grid>
        </MainCard>
      )}
    </>
  );
}
